import type { FormEvent } from "react";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Select } from "@bafe/ui";

import { Popup } from "../../components/Popup";
import { api } from "../../lib/api";
import { presentError } from "../../lib/errors";
import { queryKeys } from "../../lib/query";

function samePath(stored: string, typed: string): boolean {
	const left = stored.trim().replace(/^\/+|\/+$/g, "").toLowerCase();
	const right = typed.trim().replace(/^\/+|\/+$/g, "").toLowerCase();
	return left !== "" && left === right;
}

/** The address a visitor opens, so the redirect can be tried. Not the destination. */
function testHref(item: { hostname: string; kind: "host" | "path"; sourcePath: string }): string {
	const host = item.hostname.trim().replace(/\/+$/, "");
	if (item.kind === "path" && item.sourcePath.trim() !== "") {
		const path = item.sourcePath.startsWith("/") ? item.sourcePath : `/${item.sourcePath}`;
		return `https://${host}${path}`;
	}
	return `https://${host}/`;
}

function closeMenu(target: EventTarget) {
	if (target instanceof Element) target.closest("details")?.removeAttribute("open");
}

function RedirectActions({
	label,
	href,
	pending,
	onEdit,
	onRemove,
}: {
	label: string;
	href: string;
	pending: boolean;
	onEdit: () => void;
	onRemove: () => void;
}) {
	return (
		<td className="row-end">
			<span className="row-actions">
				<a className="open-link" href={href} target="_blank" rel="noopener noreferrer" aria-label={`Open ${label}`}>
					<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
						<path d="M14 4h6v6" />
						<path d="M20 4 10 14" />
						<path d="M18 13v6a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h6" />
					</svg>
				</a>
				<details className="menu">
					<summary aria-label={`Actions for ${label}`}>
						<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
							<circle cx="12" cy="5" r="1.8" />
							<circle cx="12" cy="12" r="1.8" />
							<circle cx="12" cy="19" r="1.8" />
						</svg>
					</summary>
					<div className="select-pop">
						<button
							className="select-item"
							type="button"
							onClick={(event) => {
								closeMenu(event.target);
								onEdit();
							}}
						>
							Edit
						</button>
						<button
							className="select-item"
							type="button"
							disabled={pending}
							onClick={(event) => {
								closeMenu(event.target);
								onRemove();
							}}
						>
							Remove
						</button>
					</div>
				</details>
			</span>
		</td>
	);
}

export function SiteRedirects() {
	const client = useQueryClient();
	const sites = useQuery({ queryKey: queryKeys.hostedSites, queryFn: () => api.listHostedSites() });
	const redirects = useQuery({ queryKey: queryKeys.siteRedirects, queryFn: () => api.listSiteRedirects() });
	const [open, setOpen] = useState(false);
	const [editingId, setEditingId] = useState<string | null>(null);
	const [siteId, setSiteId] = useState("");
	const [sourcePath, setSourcePath] = useState("");
	const [destination, setDestination] = useState("");
	const [error, setError] = useState<string | null>(null);
	const [popupError, setPopupError] = useState<string | null>(null);
	const [note, setNote] = useState<string | null>(null);

	const save = useMutation({
		mutationFn: (input: { hostedSiteId: string; sourceHost: string; destination: string; sourcePath?: string; redirectId?: string }) =>
			input.redirectId
				? api.updateSiteRedirect(input.redirectId, {
						destination: input.destination,
						sourcePath: input.sourcePath ?? "",
						enabled: true,
					})
				: api.createSiteRedirect(input),
		onSuccess: async (_saved, input) => {
			setNote(input.sourcePath ? `${input.sourcePath} now goes to that address, over HTTPS.` : "The redirect is on. It uses HTTPS.");
			setError(null);
			setPopupError(null);
			setDestination("");
			setSourcePath("");
			setSiteId("");
			setEditingId(null);
			setOpen(false);
			await client.invalidateQueries({ queryKey: queryKeys.siteRedirects });
		},
		onError: (caught) => setPopupError(presentError(caught).title),
	});
	const remove = useMutation({
		mutationFn: (id: string) => api.deleteSiteRedirect(id),
		onSuccess: async () => {
			setNote("The redirect was removed.");
			setError(null);
			await client.invalidateQueries({ queryKey: queryKeys.siteRedirects });
		},
		onError: (caught) => setError(presentError(caught).title),
	});
	function close() {
		if (save.isPending) return;
		setOpen(false);
		setEditingId(null);
		setSourcePath("");
		setPopupError(null);
	}

	function beginAdd() {
		setEditingId(null);
		setSiteId("");
		setSourcePath("");
		setDestination("");
		setPopupError(null);
		setOpen(true);
	}

	function beginEdit(item: { id: string; hostedSiteId: string; kind: "host" | "path"; sourcePath: string; destination: string }) {
		setEditingId(item.id);
		setSiteId(item.hostedSiteId);
		setSourcePath(item.kind === "path" ? item.sourcePath : "");
		setDestination(item.destination);
		setPopupError(null);
		setOpen(true);
	}

	function onSubmit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		const site = sites.data?.sites.find((item) => item.id === siteId);
		if (!site) {
			setPopupError("Choose a domain.");
			return;
		}
		if (destination.trim() === "") {
			setPopupError("Enter the address to send visitors to.");
			return;
		}
		const path = sourcePath.trim();
		const existing = editingId
			? { id: editingId }
			: path
				? redirects.data?.redirects.find(
						(item) => item.hostedSiteId === site.id && item.kind === "path" && samePath(item.sourcePath, path),
					)
				: redirects.data?.redirects.find((item) => item.hostedSiteId === site.id && item.kind === "host");
		save.mutate({
			hostedSiteId: site.id,
			sourceHost: site.hostname,
			destination,
			sourcePath: path,
			redirectId: existing?.id,
		});
	}

	if (sites.isLoading || redirects.isLoading) return <p className="empty">Loading redirects.</p>;
	if (sites.isError) return <p className="err">{presentError(sites.error).title}</p>;
	if (redirects.isError) return <p className="err">{presentError(redirects.error).title}</p>;
	const listed = sites.data?.sites ?? [];
	const rows = (redirects.data?.redirects ?? []).filter((item) => item.enabled);

	return (
		<div className="blocks">
			<div className="row head-actions trail">
				<button
					className="btn"
					type="button"
					disabled={listed.length === 0}
					onClick={beginAdd}
				>
					Add a redirect
				</button>
			</div>
			{error && <p className="err">{error}</p>}
			{note && <p className="sub">{note}</p>}
			{listed.length === 0 ? (
				<div className="card">
					<h2>No domains yet</h2>
					<p className="sub">Turn on a website for a domain in Manage. Then you can send it to another address.</p>
				</div>
			) : rows.length === 0 ? (
				<p className="empty">No redirects yet.</p>
			) : (
				<div className="table-wrap">
					<table>
						<caption className="sr-only">Redirects</caption>
						<thead>
							<tr>
								<th>Domain</th>
								<th>Path</th>
								<th>Goes to</th>
								<th></th>
							</tr>
						</thead>
						<tbody>
							{rows.map((item) => (
								<tr key={item.id}>
									<td>{item.hostname}</td>
									<td>{item.kind === "path" ? item.sourcePath : "—"}</td>
									<td>{item.destination}</td>
									<RedirectActions
										label={testHref(item)}
										href={testHref(item)}
										pending={remove.isPending}
										onEdit={() => beginEdit(item)}
										onRemove={() => remove.mutate(item.id)}
									/>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
			<Popup open={open} title={editingId ? "Edit redirect" : "Add a redirect"} onClose={close}>
				<form onSubmit={onSubmit}>
					{popupError && <p className="err">{popupError}</p>}
					<label htmlFor="redirect-domain">Domain</label>
					<Select
						id="redirect-domain"
						value={siteId}
						disabled={editingId !== null}
						onChange={(event) => {
							const next = event.target.value;
							setSiteId(next);
							if (sourcePath.trim() !== "") return;
							const current = redirects.data?.redirects.find((item) => item.hostedSiteId === next && item.kind === "host");
							setDestination(current?.destination ?? "");
						}}
					>
						<option value="">Choose a domain</option>
						{listed.map((site) => (
							<option key={site.id} value={site.id}>
								{site.hostname}
							</option>
						))}
					</Select>
					<label htmlFor="redirect-path">Path</label>
					<input
						id="redirect-path"
						value={sourcePath}
						onChange={(event) => setSourcePath(event.target.value)}
						placeholder="/link"
						autoComplete="off"
						autoFocus
					/>
					<p className="sub">Leave this empty to redirect the whole domain.</p>
					<label htmlFor="redirect-url">Send it to</label>
					<input
						id="redirect-url"
						value={destination}
						onChange={(event) => setDestination(event.target.value)}
						placeholder="https://example.com/page"
						autoComplete="off"
						inputMode="url"
					/>
					<p className="actions">
						<button className="btn" type="submit" disabled={save.isPending}>
							{save.isPending ? "Saving" : editingId ? "Save" : "Save redirect"}
						</button>
						<button className="btn ghost" type="button" onClick={close}>
							Cancel
						</button>
					</p>
				</form>
			</Popup>
		</div>
	);
}
